import { INTENT_TYPE } from '../../constants';
import { countLetters } from '../features/word-tokens';
import { type ConvergenceRule, type FeatureContext } from '../types';

/**
 * `S-EMPTY` (priority 1110) — empty or trivial input locks Search.
 *
 * The first rule in the catalog, so nothing else has to defend against empty input.
 * Trivial input covers three shapes that all mean "the user has not said anything yet"
 * — nothing at all, punctuation or symbols with no word-like token (`?`, `!!!`), and a
 * lone single-letter token (`a`).
 *
 * These two shapes are why a bare `?` and `a?` never reach the question rules. Resolving
 * them here is what lets `C-TERMINAL-QUESTION` promote a trailing question mark without
 * a word-token guard of its own: by the time it runs, the input is known to have
 * something to ask about.
 *
 * **The oversized guard is what bounds this rule's cost.** Deciding "trivial" needs the
 * word tokens, and segmentation is proportional to the input — a single unbroken
 * 100,000-character token produces one token but only after scanning all of it. Running
 * first in the catalog, this rule would pay that cost before `S-INPUT-LIMIT` ever got to
 * reject the paste. So it declines anything past the code-point bound up front, using a
 * capped scan, and lets `S-INPUT-LIMIT` report it. Segmentation therefore only ever runs
 * on input already known to be within bounds.
 */
export const isEmptyOrTrivial = (features: FeatureContext): boolean => {
	if (features.qMatch.length === 0) {
		return true;
	}

	// Oversized input is not trivial, it is oversized — and deciding otherwise would mean
	// segmenting a paste this engine has already decided not to classify.
	if (features.exceedsCodePointLimit) {
		return false;
	}

	const tokens = features.wordTokens;

	if (tokens.length === 0) {
		return true;
	}

	// One letter versus more than one is the whole question, so stop counting at two.
	return tokens.length === 1 && countLetters(tokens[0], 2) === 1;
};

export const emptyRule: ConvergenceRule = {
	type: INTENT_TYPE.FPS,
	pattern: 'EMPTY_OR_TRIVIAL',
	matches: isEmptyOrTrivial,
};
